import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Send } from "lucide-react";
import { Eyebrow, Reveal, SectionShell } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_RANGES = ["Under 18", "18-24", "25-34", "35+"];
const LEVELS = ["Professional", "Emerging Pro", "Amateur", "Youth / NIL"];
const PARTNERSHIP_TYPES = [
  "Sponsorship",
  "Athlete Ambassador",
  "Event Partnership",
  "Travel Partnership",
  "Product Collaboration",
  "Media / Content",
  "Other",
];
const BUDGETS = [
  "Under $25K",
  "$25K - $75K",
  "$75K - $250K",
  "$250K - $1M",
  "$1M+",
  "Not yet defined",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-ink/60 px-4 py-3 text-base text-white placeholder:text-slate-500 outline-none transition-all focus:border-volt/70 focus:ring-2 focus:ring-volt/20";

function Label({ children, required }) {
  return (
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
      {children}
      {required && <span className="text-volt"> *</span>}
    </span>
  );
}

function SelectWrap({ children }) {
  return (
    <div className="relative">
      {children}
      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
    </div>
  );
}

function AthleteForm() {
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = Object.fromEntries(fd.entries());
    payload.consent = fd.get("consent") === "on";
    setSending(true);
    try {
      await axios.post(`${API}/inquiries/athlete`, payload);
      window.gtag?.("event", "generate_lead", { lead_type: "athlete" });
      toast.success("Application received. Our team will be in touch soon.");
      form.reset();
    } catch (err) {
      toast.error(
        err?.response?.status === 429
          ? "Too many submissions — please try again later."
          : "Submission failed. Please email brian@smgmediagroup.com directly."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="athlete-form"
      className="grid gap-5 sm:grid-cols-2"
    >
      <label className="block">
        <Label required>Full Name</Label>
        <input required name="full_name" data-testid="athlete-full-name-input" className={inputCls} placeholder="Jordan Smith" />
      </label>
      <label className="block">
        <Label required>Email</Label>
        <input required type="email" name="email" data-testid="athlete-email-input" className={inputCls} placeholder="you@example.com" />
      </label>
      <label className="block">
        <Label required>Phone</Label>
        <input required type="tel" name="phone" data-testid="athlete-phone-input" className={inputCls} placeholder="+1 (___) ___-____" />
      </label>
      <label className="block">
        <Label required>Age Range</Label>
        <SelectWrap>
          <select required name="age_range" data-testid="athlete-age-range-select" className={`${inputCls} appearance-none pr-10`} defaultValue="">
            <option value="" disabled>Select…</option>
            {AGE_RANGES.map((a) => <option key={a}>{a}</option>)}
          </select>
        </SelectWrap>
      </label>
      <label className="block">
        <Label required>City</Label>
        <input required name="city" data-testid="athlete-city-input" className={inputCls} placeholder="Carlsbad" />
      </label>
      <label className="block">
        <Label required>State</Label>
        <input required name="state" data-testid="athlete-state-input" className={inputCls} placeholder="California" />
      </label>
      <label className="block">
        <Label required>Sport</Label>
        <input required name="sport" data-testid="athlete-sport-input" className={inputCls} defaultValue="Pickleball" />
      </label>
      <label className="block">
        <Label required>Current Level</Label>
        <SelectWrap>
          <select required name="level" data-testid="athlete-level-select" className={`${inputCls} appearance-none pr-10`} defaultValue="">
            <option value="" disabled>Select…</option>
            {LEVELS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </SelectWrap>
      </label>
      <label className="block sm:col-span-2">
        <Label>Ranking / League / Tour / Governing Association</Label>
        <input name="ranking" data-testid="athlete-ranking-input" className={inputCls} placeholder="e.g. APP Tour, UPA-A ranked…" />
      </label>
      <label className="block">
        <Label>Social Media Links</Label>
        <input name="social_links" data-testid="athlete-social-input" className={inputCls} placeholder="Instagram, TikTok, YouTube…" />
      </label>
      <label className="block">
        <Label>Website / Player Profile</Label>
        <input name="website" data-testid="athlete-website-input" className={inputCls} placeholder="https://" />
      </label>
      <label className="block">
        <Label>Current Sponsors</Label>
        <input name="sponsors" data-testid="athlete-sponsors-input" className={inputCls} placeholder="Current brand partners" />
      </label>
      <label className="block">
        <Label>Media Kit Link</Label>
        <input name="media_kit" data-testid="athlete-media-kit-input" className={inputCls} placeholder="https://" />
      </label>
      <label className="block sm:col-span-2">
        <Label required>Representation Needs</Label>
        <textarea required name="needs" rows={3} data-testid="athlete-needs-input" className={inputCls} placeholder="What do you need from representation right now?" />
      </label>
      <label className="block sm:col-span-2">
        <Label>Career Highlights</Label>
        <textarea name="highlights" rows={3} data-testid="athlete-highlights-input" className={inputCls} placeholder="Titles, medals, rankings, media features…" />
      </label>
      <label className="block sm:col-span-2">
        <Label required>Personal Statement</Label>
        <textarea required name="statement" rows={4} data-testid="athlete-statement-input" className={inputCls} placeholder="Tell us who you are, on and off the court." />
      </label>

      <div className="sm:col-span-2">
        <label className="flex items-start gap-3">
          <input required type="checkbox" name="consent" data-testid="athlete-consent-checkbox" className="mt-0.5 h-4 w-4 accent-[#84CC16]" />
          <span className="text-xs leading-relaxed text-slate-400">
            I have read and accept the SMG Media Group{" "}
            <a href="/privacy" className="text-volt underline-offset-2 hover:underline">
              privacy policy
            </a>
            . SMG Sports Management makes no guarantee of sponsorships, earnings, scholarships, rankings or professional status. Youth-athlete inquiries require a parent or legal guardian.
          </span>
        </label>
      </div>

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={sending}
          data-testid="athlete-form-submit-btn"
          className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-volt px-10 py-4 font-display text-base uppercase tracking-wider text-ink transition-all hover:bg-volt-light hover:shadow-[0_0_40px_rgba(132,204,22,0.3)] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          <Send className="h-4 w-4" />
          {sending ? "Submitting…" : "Submit Athlete Profile"}
        </button>
      </div>
    </form>
  );
}

function BrandForm() {
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = Object.fromEntries(fd.entries());
    payload.consent = fd.get("consent") === "on";
    setSending(true);
    try {
      await axios.post(`${API}/inquiries/brand`, payload);
      window.gtag?.("event", "generate_lead", { lead_type: "brand" });
      toast.success("Inquiry received. Our partnerships team will reach out shortly.");
      form.reset();
    } catch (err) {
      toast.error(
        err?.response?.status === 429
          ? "Too many submissions — please try again later."
          : "Submission failed. Please email brian@smgmediagroup.com directly."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="brand-form"
      className="grid gap-5 sm:grid-cols-2"
    >
      <label className="block">
        <Label required>Contact Name</Label>
        <input required name="contact_name" data-testid="brand-contact-name-input" className={inputCls} placeholder="Alex Rivera" />
      </label>
      <label className="block">
        <Label required>Company / Brand</Label>
        <input required name="company" data-testid="brand-company-input" className={inputCls} placeholder="Acme Sports Co." />
      </label>
      <label className="block">
        <Label required>Business Email</Label>
        <input required type="email" name="email" data-testid="brand-email-input" className={inputCls} placeholder="you@company.com" />
      </label>
      <label className="block">
        <Label>Phone</Label>
        <input type="tel" name="phone" data-testid="brand-phone-input" className={inputCls} placeholder="+1 (___) ___-____" />
      </label>
      <label className="block">
        <Label>Website</Label>
        <input name="website" data-testid="brand-website-input" className={inputCls} placeholder="https://" />
      </label>
      <label className="block">
        <Label required>Partnership Type</Label>
        <SelectWrap>
          <select required name="partnership_type" data-testid="brand-partnership-type-select" className={`${inputCls} appearance-none pr-10`} defaultValue="">
            <option value="" disabled>Select…</option>
            {PARTNERSHIP_TYPES.map((p) => <option key={p}>{p}</option>)}
          </select>
        </SelectWrap>
      </label>
      <label className="block">
        <Label required>Target Audience</Label>
        <input required name="audience" data-testid="brand-audience-input" className={inputCls} placeholder="Who are you trying to reach?" />
      </label>
      <label className="block">
        <Label required>Estimated Budget Range</Label>
        <SelectWrap>
          <select required name="budget" data-testid="brand-budget-select" className={`${inputCls} appearance-none pr-10`} defaultValue="">
            <option value="" disabled>Select…</option>
            {BUDGETS.map((b) => <option key={b}>{b}</option>)}
          </select>
        </SelectWrap>
      </label>
      <label className="block sm:col-span-2">
        <Label>Campaign Timing</Label>
        <input name="timing" data-testid="brand-timing-input" className={inputCls} placeholder="e.g. Q3 2026 launch, season-long…" />
      </label>
      <label className="block sm:col-span-2">
        <Label required>Athlete Profile Sought</Label>
        <textarea required name="athlete_profile" rows={3} data-testid="brand-athlete-profile-input" className={inputCls} placeholder="Describe the athlete profile you're looking for." />
      </label>
      <label className="block sm:col-span-2">
        <Label required>Project Brief</Label>
        <textarea required name="brief" rows={4} data-testid="brand-brief-input" className={inputCls} placeholder="Goals, deliverables, timeline — the shape of the partnership." />
      </label>

      <div className="sm:col-span-2">
        <label className="flex items-start gap-3">
          <input required type="checkbox" name="consent" data-testid="brand-consent-checkbox" className="mt-0.5 h-4 w-4 accent-[#84CC16]" />
          <span className="text-xs leading-relaxed text-slate-400">
            I have read and accept the SMG Media Group{" "}
            <a href="/privacy" className="text-volt underline-offset-2 hover:underline">
              privacy policy
            </a>
            .
          </span>
        </label>
      </div>

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={sending}
          data-testid="brand-form-submit-btn"
          className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-volt px-10 py-4 font-display text-base uppercase tracking-wider text-ink transition-all hover:bg-volt-light hover:shadow-[0_0_40px_rgba(132,204,22,0.3)] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          <Send className="h-4 w-4" />
          {sending ? "Submitting…" : "Explore Athlete Partnerships"}
        </button>
      </div>
    </form>
  );
}

export default function InquiryForms() {
  const [tab, setTab] = useState(() =>
    window.location.hash === "#partners" ? "brand" : "athlete"
  );

  useEffect(() => {
    const sync = () =>
      setTab(window.location.hash === "#partners" ? "brand" : "athlete");
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  return (
    <SectionShell id="apply">
      <Reveal>
        <Eyebrow className="mb-5">— 06 / Get In The Room</Eyebrow>
        <h2 className="font-display text-4xl uppercase leading-[1.05] sm:text-5xl lg:text-6xl">
          Two paths. <span className="text-volt">Both start here.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.15}>
        <span id="partners" aria-hidden="true" className="block h-0" />
        <div
          className="mx-auto mt-12 max-w-4xl rounded-3xl border border-white/10 bg-surface p-6 sm:p-10"
          data-testid="inquiry-forms-panel"
        >
          <div className="mb-8 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-ink/60 p-2">
            <button
              onClick={() => setTab("athlete")}
              data-testid="tab-athlete"
              className={`rounded-xl px-4 py-3.5 font-display text-sm uppercase tracking-wider transition-all sm:text-base ${
                tab === "athlete" ? "bg-volt text-ink" : "text-slate-400 hover:text-white"
              }`}
            >
              For Athletes
            </button>
            <button
              onClick={() => setTab("brand")}
              data-testid="tab-brand"
              className={`rounded-xl px-4 py-3.5 font-display text-sm uppercase tracking-wider transition-all sm:text-base ${
                tab === "brand" ? "bg-volt text-ink" : "text-slate-400 hover:text-white"
              }`}
            >
              For Brands & Partners
            </button>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {tab === "athlete" ? (
                <div>
                  <p className="mb-8 text-sm leading-relaxed text-slate-400" data-testid="athlete-form-intro">
                    Professional, emerging, amateur and NIL talent — apply for
                    representation below.
                  </p>
                  <AthleteForm />
                </div>
              ) : (
                <div>
                  <p className="mb-8 text-sm leading-relaxed text-slate-400" data-testid="brand-form-intro">
                    Sponsorships, ambassadors, event partnerships, product
                    collaborations and media integrations — tell us what you're
                    building.
                  </p>
                  <BrandForm />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </Reveal>
    </SectionShell>
  );
}
